/**
 * guestProgress.js — sessions played on a public /games/:id page, before the
 * visitor has an account.
 *
 * A visitor arriving from search can now play without signing up, so their
 * results have nowhere to go: `saveSession` needs a kid id and returns early
 * without one. Rather than throw the play away, we keep it here and hand it to
 * the backend once a profile exists — which is also what makes the win-screen
 * ask worth answering ("you earned 12 stars — keep them").
 *
 * localStorage only. Nothing here is authoritative: the server recomputes gems
 * from the session bounds when the sessions are finally posted, so a visitor
 * editing this cannot mint currency.
 */
import { sessionsApi } from "../api/client";

const KEY = "guest_sessions_v1";
// A visitor who plays this much has told us plenty; keep the payload small so
// the post-signup flush stays one quick burst rather than a stampede.
const MAX_SESSIONS = 30;

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX_SESSIONS)));
  } catch {
    /* private mode, or quota — guest progress is a nice-to-have, never a blocker */
  }
}

/** Record one finished guest game. Returns the running totals. */
export function recordGuestSession(session) {
  const list = read();
  list.push({ ...session, played_at: new Date().toISOString() });
  write(list);
  return guestTotals();
}

export function getGuestSessions() {
  return read();
}

/** Totals shown in the signup prompt. Gems mirror the backend rule: stars × 5. */
export function guestTotals() {
  const list = read();
  return {
    games: list.length,
    stars: list.reduce((n, s) => n + (s.stars || 0), 0),
    gems: list.reduce((n, s) => n + (s.stars || 0) * 5, 0),
  };
}

export function clearGuestSessions() {
  try { localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

/**
 * Post every stored guest session to a freshly created kid, oldest first, then
 * clear them. Best-effort: a failure leaves the sessions in place so the next
 * login can retry, and never blocks the caller's flow.
 */
export async function flushGuestSessions(kidId) {
  const list = read();
  if (!kidId || !list.length) return { posted: 0 };
  let posted = 0;
  for (const s of list) {
    try {
      await sessionsApi.save(kidId, {
        game: s.game, subject: s.subject, score: s.score, total: s.total,
        stars: s.stars, duration: s.duration,
        ...(s.items?.length ? { items: s.items } : {}),
        ...(s.end_level ? { end_level: s.end_level } : {}),
        ...(s.game_id ? { game_id: s.game_id } : {}),
      });
      posted += 1;
    } catch {
      // Drop what already landed and keep only the rest. Returning without
      // trimming left the posted ones in the store, so the next flush sent
      // them again and the kid was credited twice for the same games.
      write(list.slice(posted));
      return { posted, remaining: list.length - posted };
    }
  }
  clearGuestSessions();
  return { posted, remaining: 0 };
}
